import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import PurchaseRequestsTable from '../../components/purchaseRequests/PurchaseRequestsTable';
import NewRequestModal from '../../components/purchaseRequests/NewRequestModal';
import { PulseLoader } from "react-spinners";

const AllPurchaseRequests = () => {
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [noQuotationsOnly, setNoQuotationsOnly] = useState(false);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/purchase-requests`);
      setRequests(res.data);
      // Check for success flag in sessionStorage
      if (sessionStorage.getItem('requestSuccess') === 'true') {
        toast.success('Request added successfully!');
        sessionStorage.removeItem('requestSuccess');
      }
    } catch (err) {
      console.error("Error fetching purchase requests", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);
  
  // Derived filtered list
  const allRequests = requests.requests || [];
  const filteredRequests = allRequests.filter(r => {
    // Search across request number, requester name, priority, notes and product names
    if (searchText) {
      const q = searchText.toLowerCase();
      const requester = (r.requested_by?.name || '').toLowerCase();
      const priority = (r.priority || '').toLowerCase();
      const notes = (r.notes || '').toLowerCase();
      const requestNo = (r.request_no || '').toLowerCase();
      const soNumber = (r.soId?.orderNumber || '').toLowerCase();
      const itemsText = (Array.isArray(r.items) ? r.items.map(it => (it.product?.productName || '')).join(' ') : '').toLowerCase();

      if (!requester.includes(q) && !priority.includes(q) && !notes.includes(q) && !requestNo.includes(q) && !soNumber.includes(q) && !itemsText.includes(q)) return false;
    }

    // Priority filter
    if (priorityFilter !== 'all' && (r.priority || '').toLowerCase() !== priorityFilter) return false;

    // Date range filter (required_by_date)
    if (dateFrom) {
      const from = new Date(dateFrom);
      const reqDate = new Date(r.required_by_date);
      if (reqDate < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      const reqDate = new Date(r.required_by_date);
      // include the day
      to.setHours(23,59,59,999);
      if (reqDate > to) return false;
    }

    // No quotations filter - check server-provided flags/rfq arrays
    if (noQuotationsOnly) {
      const hasQuotes = (r.has_rfq === true) || (Number(r.rfq_count) > 0) || (Array.isArray(r.rfq_ids) && r.rfq_ids.length > 0) || (Array.isArray(r.quotations) && r.quotations.length > 0) || (Array.isArray(r.responses) && r.responses.length > 0);
      if (hasQuotes) return false;
    }

    return true;
  });
  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Purchase Requests</li>
        </ol>
      </nav>

      <Row className="mb-4">
        <Col md={6}><h2>Purchase Requests</h2></Col>
        <Col className="text-end">
          <Button onClick={() => setShowModal(true)}>
            <i className="fas fa-plus me-2" /> Rise Request
          </Button>
        </Col>
      </Row>
      {/* Filters */}
      <Row className="mb-3 g-2 align-items-center">
        <Col md={3}>
          <input className="form-control" placeholder="Search requester, priority or notes" value={searchText} onChange={e => setSearchText(e.target.value)} />
        </Col>
        <Col md={2}>
          <select className="form-select" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Col>
        <Col md={2}>
          <input type="date" className="form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </Col>
        <Col md={2}>
          <input type="date" className="form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </Col>
        <Col md={2}>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="noQuotes" checked={noQuotationsOnly} onChange={e => setNoQuotationsOnly(e.target.checked)} />
            <label className="form-check-label" htmlFor="noQuotes">New - No Quotations</label>
          </div>
        </Col>
        <Col md={1} className="text-end">
          <button className="btn btn-outline-secondary" onClick={() => { setSearchText(''); setPriorityFilter('all'); setDateFrom(''); setDateTo(''); setNoQuotationsOnly(false); }} title="Reset filters">Reset</button>
        </Col>
      </Row>
      {isLoading ? (
        <PulseLoader
          color="#014b7cbb"
          size={9}
        />
      ) : (
        <PurchaseRequestsTable requests={{ requests: filteredRequests }} />
      )}

      <NewRequestModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onRequestAdded={fetchRequests}
      />
    </Layout>
  );
};

export default AllPurchaseRequests;
